import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flag, Mic, Volume2, VolumeX } from 'lucide-react';
import { useAskAiVoice } from '../hooks/useAskAiVoice';
import { unlockBrowserSpeech } from '../utils/browserSpeech';
import AaCoreAiName from './AaCoreAiName';
import AskAiRichText from './AskAiRichText';
import AskAiBookingReviewCard from './AskAiBookingReviewCard';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { getBrowserTimeZone, formatLocalYmd } from '../utils/timezoneUtils';
import { askAiRequestErrorMessage } from '../utils/askAiErrors';
import { toProfessionalWording } from '../utils/askAiWording';
import { captureAskAiChatImages } from '../utils/captureAskAiChat';
import { clearAskAiSessionId, getOrCreateAskAiSessionId, resetAskAiSessionId } from '../utils/askAiSession';
import { AskAiMobileFullscreenButton, AskAiMobileScrim, useAskAiMobileFullscreen } from './AskAiMobileFullscreen';
import {
  AskAiAction,
  AskAiBookingDraft,
  AskAiBookingService,
  AskAiBookingServiceGroup,
  AskAiBookingStatus,
  AskAiBookingTechnicianGroup,
  AskAiContactDraft,
  AskAiReply,
  AskAiSuggestion,
  AskAiTurn,
} from '../types';

interface TenantAskAiWidgetProps {
  shortName: string;
  businessName: string;
  enabled?: boolean;
  showContactHint?: boolean;
  accentColor?: string;
  textColor?: string;
  placement?: 'default' | 'booking';
}

function parseHex(color: string): { r: number; g: number; b: number } | null {
  const raw = String(color || '').trim().replace('#', '');
  if (raw.length === 3) {
    return {
      r: parseInt(raw[0] + raw[0], 16),
      g: parseInt(raw[1] + raw[1], 16),
      b: parseInt(raw[2] + raw[2], 16),
    };
  }
  if (raw.length === 6) {
    return {
      r: parseInt(raw.slice(0, 2), 16),
      g: parseInt(raw.slice(2, 4), 16),
      b: parseInt(raw.slice(4, 6), 16),
    };
  }
  return null;
}

function contrastInk(color: string): string {
  const rgb = parseHex(color);
  if (!rgb) return '#ffffff';
  const luma = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luma > 0.62 ? '#111827' : '#ffffff';
}

function hourInTimeZone(timeZone?: string): number {
  try {
    const zone = String(timeZone || '').trim();
    const parts = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hourCycle: 'h23',
      ...(zone ? { timeZone: zone } : {}),
    }).formatToParts(new Date());
    const hour = parseInt(parts.find((part) => part.type === 'hour')?.value || '', 10);
    if (Number.isFinite(hour)) return hour;
  } catch {
    // fall through
  }
  return new Date().getHours();
}

function sessionGreeting(firstName?: string, businessName?: string): string {
  const hour = hourInTimeZone(getBrowserTimeZone());
  const hello = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const name = String(firstName || '').trim();
  const place = String(businessName || '').trim();
  const assist = place
    ? `I am here to assist you with your needs at ${place}. Ask me about services, hours, or booking an appointment.`
    : 'I am here to assist you with your needs. Ask me about services, hours, or booking an appointment.';
  return name ? `${hello} ${name}, ${assist}` : `${hello}, ${assist}`;
}

function BookingChangeLinks({
  accentColor,
  accentInk,
  textColor,
  disabled,
  quoteMode,
  seriesMode,
  onSite,
  onChange,
}: {
  accentColor: string;
  accentInk: string;
  textColor: string;
  disabled?: boolean;
  quoteMode?: boolean;
  seriesMode?: boolean;
  onSite?: boolean;
  onChange: (query: string, override: AskAiBookingDraft) => void;
}) {
  const actions: Array<{ label: string; query: string; override: AskAiBookingDraft }> = quoteMode
    ? [
        {
          label: 'Change services',
          query: 'Change services',
          override: {
            wanted: true,
            revisingServices: true,
            resetQuoteDetails: true,
            resetPreferredSchedule: true,
            quoteDetails: '',
            preferredSchedule: '',
          },
        },
        {
          label: 'Change details',
          query: 'Change details',
          override: { wanted: true, resetQuoteDetails: true, quoteDetails: '' },
        },
        {
          label: 'Change schedule',
          query: 'Change schedule',
          override: { wanted: true, resetPreferredSchedule: true, preferredSchedule: '' },
        },
      ]
    : [
        {
          label: 'Change services',
          query: 'Change services',
          override: {
            wanted: true,
            revisingServices: true,
          },
        },
        {
          label: 'Change professional',
          query: 'Change professional',
          override: {
            wanted: true,
            resetProfessional: true,
            professionalId: '',
            professionalName: '',
            serviceProfessionals: [],
          },
        },
        {
          label: 'Change date',
          query: 'Change date',
          override: { wanted: true, date: '', time: '' },
        },
        {
          label: 'Change time',
          query: 'Change time',
          override: { wanted: true, time: '' },
        },
        ...(onSite
          ? [{
              label: 'Change address',
              query: 'Change address',
              override: { wanted: true, resetServiceAddress: true, serviceAddress: undefined },
            }]
          : []),
        ...(seriesMode
          ? [
              {
                label: 'Change how often',
                query: 'Change how often',
                override: { wanted: true, recurring: true, resetIntervalWeeks: true },
              },
              {
                label: 'Change visits',
                query: 'Change visits',
                override: { wanted: true, recurring: true, resetOccurrenceCount: true },
              },
              {
                label: 'One-time visit',
                query: 'One-time visit',
                override: { wanted: true, recurring: false, resetRecurring: true },
              },
            ]
          : [
              {
                label: 'Make this recurring',
                query: 'Make this recurring',
                override: { wanted: true, recurring: true },
              },
            ]),
        {
          label: 'Start over',
          query: 'Start over',
          override: { wanted: true },
        },
      ];
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {actions.map((action) => (
        <button
          key={action.label}
          type="button"
          disabled={disabled}
          className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
          style={{ borderColor: `${textColor}22`, color: textColor }}
          onClick={() => onChange(action.query, action.override)}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}

function QuoteInstructions({
  items,
  textColor,
}: {
  items: Array<{ serviceName: string; instructions: string }>;
  textColor: string;
}) {
  if (!items.length) return null;
  const muted = `${textColor}99`;
  const line = `${textColor}18`;
  return (
    <div className="mt-2 overflow-hidden" style={{ border: `1px solid ${line}`, borderRadius: 12, background: `${textColor}06` }}>
      <div className="px-3 py-2 text-xs font-semibold tracking-wide uppercase" style={{ color: muted, borderBottom: `1px solid ${line}` }}>
        Quote request instructions
      </div>
      <div className="px-3 py-2 space-y-2">
        {items.map((item) => (
          <div key={item.serviceName}>
            <p className="m-0 text-sm font-medium">{item.serviceName}</p>
            <p className="m-0 mt-0.5 text-xs whitespace-pre-wrap" style={{ color: muted }}>{item.instructions}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuoteImageAttach({
  booking,
  accentColor,
  accentInk,
  textColor,
  disabled,
  onUploaded,
}: {
  booking: AskAiBookingStatus;
  accentColor: string;
  accentInk: string;
  textColor: string;
  disabled?: boolean;
  onUploaded: (attachmentIds: string[], imageUrls: string[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const muted = `${textColor}99`;
  const count = Math.max(booking.quoteImageUrls?.length || 0, booking.quoteAttachmentIds?.length || 0);
  const remaining = Math.max(0, 10 - count);
  return (
    <div className="mt-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        multiple
        className="hidden"
        onChange={async (event) => {
          const files = Array.from(event.target.files || []).slice(0, remaining);
          if (files.length === 0 || !booking.businessId) return;
          setUploading(true);
          try {
            const uploaded = await apiService.uploadQuoteImages(booking.businessId, files);
            onUploaded(
              [...(booking.quoteAttachmentIds || []), ...(uploaded.attachmentIds || [])].slice(0, 10),
              [...(booking.quoteImageUrls || []), ...(uploaded.urls || [])].slice(0, 10)
            );
          } catch {
            // parent status line handles failures via onUploaded not firing
          } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = '';
          }
        }}
      />
      <button
        type="button"
        className="text-xs font-semibold px-2 py-1 cursor-pointer disabled:opacity-60"
        style={{ background: accentColor, color: accentInk, borderRadius: 8, border: 0 }}
        disabled={disabled || uploading || remaining <= 0}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? 'Uploading…' : remaining <= 0 ? 'Max 10 photos' : count > 0 ? 'Add more photos' : 'Add photos (optional)'}
      </button>
      {count > 0 ? (
        <p className="m-0 mt-1 text-xs" style={{ color: muted }}>{count} photo{count === 1 ? '' : 's'} attached</p>
      ) : null}
    </div>
  );
}

function ServicePicker({
  groups,
  preselectedIds,
  accentColor,
  accentInk,
  textColor,
  disabled,
  onContinue,
}: {
  groups: AskAiBookingServiceGroup[];
  preselectedIds?: string[];
  accentColor: string;
  accentInk: string;
  textColor: string;
  disabled?: boolean;
  onContinue: (services: AskAiBookingService[]) => void;
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>(() => preselectedIds || []);
  const muted = `${textColor}99`;
  const line = `${textColor}18`;
  const byId = new Map(groups.flatMap((group) => group.services.map((service) => [service.id, service] as const)));
  const selected = selectedIds.map((id) => byId.get(id)).filter(Boolean) as AskAiBookingService[];
  const hasMain = selected.some((service) => !service.isAddOn);

  const toggle = (id: string) => {
    setSelectedIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  return (
    <div className="mt-2" style={{ border: `1px solid ${line}`, borderRadius: 12, background: `${textColor}06` }}>
      <div className="max-h-64 overflow-y-auto px-2 py-2">
        {groups.map((group) => (
          <div key={group.category} className="mb-2 last:mb-0">
            <p className="m-0 px-1 pb-1 text-[11px] font-semibold tracking-wide uppercase" style={{ color: accentColor }}>
              {group.category}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {group.services.map((service) => {
                const on = selectedIds.includes(service.id);
                return (
                  <button
                    key={service.id}
                    type="button"
                    disabled={disabled}
                    className="text-xs border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60 text-left"
                    style={
                      on
                        ? { background: accentColor, color: accentInk, borderColor: accentColor }
                        : { background: 'transparent', color: textColor, borderColor: `${textColor}22` }
                    }
                    onClick={() => toggle(service.id)}
                  >
                    {service.name}
                    {service.isAddOn ? ' · add-on' : ''}
                    {service.quotable ? ' · quote' : ''}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="px-2 pb-2 pt-1" style={{ borderTop: `1px solid ${line}` }}>
        {!hasMain && selected.length > 0 ? (
          <p className="m-0 mb-2 text-xs" style={{ color: muted }}>
            Add a main service to continue with those add-ons.
          </p>
        ) : null}
        <button
          type="button"
          disabled={disabled || selected.length === 0 || !hasMain}
          className="text-sm font-semibold px-3 py-2 w-full cursor-pointer disabled:opacity-60"
          style={{ background: accentColor, color: accentInk, borderRadius: 10, border: 0 }}
          onClick={() => onContinue(selected)}
        >
          {selected.length > 1 ? `Continue with ${selected.length} services` : selected.length === 1 ? 'Continue with this service' : 'Select services'}
        </button>
      </div>
    </div>
  );
}

function TechnicianPicker({
  groups,
  accentColor,
  accentInk,
  textColor,
  disabled,
  onContinue,
}: {
  groups: AskAiBookingTechnicianGroup[];
  accentColor: string;
  accentInk: string;
  textColor: string;
  disabled?: boolean;
  onContinue: (picks: Array<{ serviceId: string; professionalId: string; professionalName: string }>) => void;
}) {
  const [selectedByService, setSelectedByService] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const group of groups) {
      if (group.selectedId) initial[group.serviceId] = group.selectedId;
      else if (group.professionals.length === 1) initial[group.serviceId] = group.professionals[0].id;
    }
    return initial;
  });
  const muted = `${textColor}99`;
  const line = `${textColor}18`;
  const complete = groups.every((group) => Boolean(selectedByService[group.serviceId]));

  return (
    <div className="mt-2" style={{ border: `1px solid ${line}`, borderRadius: 12, background: `${textColor}06` }}>
      <div className="max-h-64 overflow-y-auto px-2 py-2">
        {groups.map((group) => (
          <div key={group.serviceId} className="mb-2 last:mb-0">
            <p className="m-0 px-1 pb-1 text-[11px] font-semibold tracking-wide uppercase" style={{ color: muted }}>
              {group.serviceName}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {group.professionals.map((person) => {
                const on = selectedByService[group.serviceId] === person.id;
                return (
                  <button
                    key={person.id}
                    type="button"
                    disabled={disabled}
                    className="text-xs border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                    style={
                      on
                        ? { background: accentColor, color: accentInk, borderColor: accentColor }
                        : { background: 'transparent', color: textColor, borderColor: `${textColor}22` }
                    }
                    onClick={() => setSelectedByService((current) => ({ ...current, [group.serviceId]: person.id }))}
                  >
                    {person.name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="px-2 pb-2 pt-1" style={{ borderTop: `1px solid ${line}` }}>
        <button
          type="button"
          disabled={disabled || !complete}
          className="text-sm font-semibold px-3 py-2 w-full cursor-pointer disabled:opacity-60"
          style={{ background: accentColor, color: accentInk, borderRadius: 10, border: 0 }}
          onClick={() => {
            const picks = groups
              .map((group) => {
                const professionalId = selectedByService[group.serviceId];
                const person = group.professionals.find((item) => item.id === professionalId);
                if (!professionalId || !person) return undefined;
                return { serviceId: group.serviceId, professionalId, professionalName: person.name };
              })
              .filter(Boolean) as Array<{ serviceId: string; professionalId: string; professionalName: string }>;
            onContinue(picks);
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function timeChipLabel(hhmm: string): string {
  const [hRaw, mRaw] = String(hhmm || '').split(':');
  const hour = parseInt(hRaw, 10);
  const minute = (mRaw || '00').slice(0, 2);
  if (Number.isNaN(hour)) return hhmm;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${minute} ${ampm}`;
}

function looksLikeTimeChip(value: string): boolean {
  const raw = String(value || '').trim();
  return /\b\d{1,2}:\d{2}\s*(am|pm)\b/i.test(raw) || /^([01]?\d|2[0-3]):[0-5]\d$/.test(raw);
}

function TimeSlotGrid({
  times,
  textColor,
  disabled,
  onPick,
}: {
  times: string[];
  textColor: string;
  disabled?: boolean;
  onPick: (time: string, label: string) => void;
}) {
  if (times.length === 0) return null;
  const line = `${textColor}22`;
  return (
    <div className="grid grid-cols-3 gap-1.5 mt-2">
      {times.map((time) => {
        const label = looksLikeTimeChip(time) && /am|pm/i.test(time) ? time : timeChipLabel(time);
        return (
          <button
            key={time}
            type="button"
            disabled={disabled}
            className="text-xs bg-transparent border px-1.5 py-1.5 cursor-pointer disabled:opacity-60"
            style={{ borderColor: line, color: textColor, borderRadius: 8 }}
            onClick={() => onPick(time, label)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function DateChoicePicker({
  options,
  showCalendar,
  accentColor,
  accentInk,
  textColor,
  disabled,
  onPick,
}: {
  options: Array<{ label: string; date: string }>;
  showCalendar?: boolean;
  accentColor: string;
  accentInk: string;
  textColor: string;
  disabled?: boolean;
  onPick: (ymd: string, label: string) => void;
}) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const todayYmd = formatLocalYmd(new Date());
  const min = options[0]?.date && options[0].date <= todayYmd ? options[0].date : todayYmd;
  const minDate = parseLocalYmd(min);
  const [monthCursor, setMonthCursor] = useState(() => new Date(minDate.getFullYear(), minDate.getMonth(), 1));
  const muted = `${textColor}99`;
  const line = `${textColor}22`;
  const maxDate = new Date(minDate.getFullYear(), minDate.getMonth() + 12, minDate.getDate());

  useEffect(() => {
    if (!calendarOpen) return;
    calendarRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [calendarOpen]);

  const canPrev = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1) > new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const canNext = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1) < new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
  const cells = monthCells(monthCursor.getFullYear(), monthCursor.getMonth());
  const monthTitle = monthCursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={option.date}
            type="button"
            disabled={disabled}
            className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
            style={{ borderColor: line, color: textColor }}
            onClick={() => onPick(option.date, option.label)}
          >
            {option.label}
          </button>
        ))}
        {showCalendar ? (
          <button
            type="button"
            disabled={disabled}
            className="text-xs font-semibold border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
            style={
              calendarOpen
                ? { background: accentColor, color: accentInk, borderColor: accentColor }
                : { background: 'transparent', color: textColor, borderColor: line }
            }
            onClick={() => setCalendarOpen((open) => !open)}
          >
            Show Calendar
          </button>
        ) : null}
      </div>
      {showCalendar && calendarOpen && !disabled ? (
        <div
          ref={calendarRef}
          className="mt-2 px-2 py-2"
          style={{ border: `1px solid ${line}`, borderRadius: 12, background: `${textColor}06`, scrollMarginBottom: 88 }}
        >
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              className="text-sm px-2 py-0.5 bg-transparent border-0 cursor-pointer disabled:opacity-30"
              style={{ color: textColor }}
              disabled={!canPrev}
              aria-label="Previous month"
              onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))}
            >
              ‹
            </button>
            <p className="m-0 text-sm font-semibold">{monthTitle}</p>
            <button
              type="button"
              className="text-sm px-2 py-0.5 bg-transparent border-0 cursor-pointer disabled:opacity-30"
              style={{ color: textColor }}
              disabled={!canNext}
              aria-label="Next month"
              onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))}
            >
              ›
            </button>
          </div>
          <div className="grid grid-cols-7 gap-y-1 text-center">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div key={day} className="text-[10px] font-semibold" style={{ color: muted }}>{day}</div>
            ))}
            {cells.map((cell, index) => {
              if (!cell.ymd) return <div key={`e-${index}`} />;
              const tooEarly = cell.ymd < min;
              const tooLate = cell.ymd > formatLocalYmd(maxDate);
              const isToday = cell.ymd === todayYmd;
              return (
                <button
                  key={cell.ymd}
                  type="button"
                  disabled={tooEarly || tooLate}
                  className="text-xs h-7 rounded-full border-0 cursor-pointer disabled:opacity-30 disabled:cursor-default"
                  style={
                    isToday
                      ? { background: accentColor, color: accentInk }
                      : { background: 'transparent', color: textColor }
                  }
                  onClick={() => {
                    onPick(cell.ymd!, cell.ymd!);
                    setCalendarOpen(false);
                  }}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function parseLocalYmd(ymd: string): Date {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

function monthCells(year: number, month: number): Array<{ day?: number; ymd?: string }> {
  const first = new Date(year, month, 1);
  const pad = first.getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();
  const cells: Array<{ day?: number; ymd?: string }> = [];
  for (let i = 0; i < pad; i += 1) cells.push({});
  for (let day = 1; day <= lastDate; day += 1) {
    cells.push({ day, ymd: formatLocalYmd(new Date(year, month, day)) });
  }
  return cells;
}

function indexRecord(value: unknown): Record<number, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<number, unknown> = {};
  Object.keys(value as object).forEach((key) => {
    const index = Number(key);
    const item = (value as Record<string, unknown>)[key];
    if (!Number.isInteger(index) || index < 0 || !item || typeof item !== 'object') return;
    out[index] = item;
  });
  return out;
}

function sanitizeAction(value: unknown): AskAiAction | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as AskAiAction;
  if (!raw.url) return null;
  return { label: String(raw.label || 'Open'), url: String(raw.url) };
}

function sanitizeSuggestion(value: unknown): AskAiSuggestion | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as AskAiSuggestion;
  const primary = sanitizeAction(raw.primary);
  const actions = Array.isArray(raw.actions)
    ? raw.actions.map(sanitizeAction).filter(Boolean) as AskAiAction[]
    : [];
  if (!raw.short_name && !primary && actions.length === 0) return null;
  return {
    short_name: String(raw.short_name || ''),
    name: String(raw.name || ''),
    category: raw.category ? String(raw.category) : undefined,
    reason: raw.reason ? String(raw.reason) : undefined,
    path: raw.path ? String(raw.path) : undefined,
    primary: primary || undefined,
    actions,
  };
}

const TENANT_ASK_VISIT_KEY = 'booking-ask:active-tenant';
export const TENANT_ASK_AI_OPEN_EVENT = 'aacore:open-tenant-ask-ai';
const tenantAskMounts: Record<string, number> = {};

export function openTenantAskAi(shortName?: string): void {
  window.dispatchEvent(new CustomEvent(TENANT_ASK_AI_OPEN_EVENT, { detail: { shortName } }));
}

function holdTenantAskOnPath(path: string): boolean {
  const p = String(path || '');
  return p.startsWith('/login') || p === '/dashboard/customer/profile' || p.startsWith('/dashboard/customer/profile/');
}

function isTenantPath(path: string, shortName: string): boolean {
  const name = String(shortName || '').trim().toLowerCase();
  if (!name) return false;
  const clean = String(path || '').split('?')[0].split('#')[0].replace(/\/+$/, '').toLowerCase() || '/';
  return clean === `/${name}` || clean.startsWith(`/${name}/`);
}

function persistTenantAskOpen(storageKey: string, open: boolean): void {
  if (open) sessionStorage.setItem(`${storageKey}:open`, '1');
  else sessionStorage.removeItem(`${storageKey}:open`);
}

function persistTenantAskHold(storageKey: string, hold: boolean): void {
  if (hold) sessionStorage.setItem(`${storageKey}:hold`, '1');
  else sessionStorage.removeItem(`${storageKey}:hold`);
}

function shouldHoldTenantAsk(storageKey: string): boolean {
  return sessionStorage.getItem(`${storageKey}:hold`) === '1' || holdTenantAskOnPath(window.location.pathname);
}

function clearTenantAskStorage(storageKey: string): void {
  sessionStorage.removeItem(storageKey);
  sessionStorage.removeItem(`${storageKey}:contact`);
  sessionStorage.removeItem(`${storageKey}:booking`);
  sessionStorage.removeItem(`${storageKey}:extras`);
  sessionStorage.removeItem(`${storageKey}:open`);
  sessionStorage.removeItem(`${storageKey}:hold`);
  sessionStorage.removeItem(`${storageKey}:flagged`);
  clearAskAiSessionId(storageKey);
}

function loadChatExtras(key: string): {
  suggestions: Record<number, AskAiSuggestion>;
  bookings: Record<number, AskAiBookingStatus>;
} {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (!parsed || typeof parsed !== 'object') return { suggestions: {}, bookings: {} };
    const suggestions: Record<number, AskAiSuggestion> = {};
    Object.entries(indexRecord((parsed as { suggestions?: unknown }).suggestions)).forEach(([key, item]) => {
      const suggestion = sanitizeSuggestion(item);
      if (suggestion) suggestions[Number(key)] = suggestion;
    });
    const bookings = indexRecord((parsed as { bookings?: unknown }).bookings) as Record<number, AskAiBookingStatus>;
    return { suggestions, bookings };
  } catch {
    return { suggestions: {}, bookings: {} };
  }
}

const TenantAskAiWidget: React.FC<TenantAskAiWidgetProps> = ({
  shortName,
  businessName,
  enabled,
  showContactHint: _showContactHint = true,
  accentColor = '#e11d48',
  textColor = '#111827',
  placement = 'default',
}) => {
  const { user } = useAuth();
  const [isEnabled, setIsEnabled] = useState<boolean>(Boolean(enabled));
  const [open, setOpen] = useState(() => {
    try {
      return sessionStorage.getItem(`booking-ask:${shortName}:open`) === '1';
    } catch {
      return false;
    }
  });
  const [attractAttention, setAttractAttention] = useState(() => {
    try {
      return sessionStorage.getItem(`booking-ask:${shortName}:open`) !== '1';
    } catch {
      return true;
    }
  });
  const [bounceTick, setBounceTick] = useState<{ id: number; height: number; duration: number } | null>(null);
  const [pending, setPending] = useState(false);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [statusError, setStatusError] = useState(false);
  const [flagging, setFlagging] = useState(false);
  const [conversationFlagged, setConversationFlagged] = useState(false);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<AskAiTurn[]>([]);
  const [contactDraft, setContactDraft] = useState<AskAiContactDraft>({});
  const [bookingDraft, setBookingDraft] = useState<AskAiBookingDraft>({});
  const [suggestionsByIndex, setSuggestionsByIndex] = useState<Record<number, AskAiSuggestion>>({});
  const [bookingByIndex, setBookingByIndex] = useState<Record<number, AskAiBookingStatus>>({});
  const dockRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastFocusRef = useRef<HTMLElement | null>(null);
  const resumedBookingRef = useRef(false);
  const askRef = useRef<(query: string, bookingOverride?: AskAiBookingDraft) => Promise<void>>(async () => undefined);
  const listenBaseRef = useRef('');
  const voice = useAskAiVoice({
    active: open,
    onInterimTranscript: (text) => {
      setInput([listenBaseRef.current, text].filter(Boolean).join(' ').trim());
    },
    onFinalTranscript: (text) => {
      const next = [listenBaseRef.current, text].filter(Boolean).join(' ').trim();
      listenBaseRef.current = '';
      if (next) {
        setInput('');
        void askRef.current(next);
      }
    },
  });
  const { setSpeechError, stopListening, stopSpeaking, speak } = voice;
  const speakRef = useRef(speak);
  speakRef.current = speak;

  const storageKey = `booking-ask:${shortName}`;
  const contactKey = `${storageKey}:contact`;
  const bookingKey = `${storageKey}:booking`;
  const extrasKey = `${storageKey}:extras`;
  const suggestionsRef = useRef<Record<number, AskAiSuggestion>>({});
  const bookingsRef = useRef<Record<number, AskAiBookingStatus>>({});
  const accentInk = useMemo(() => contrastInk(accentColor), [accentColor]);
  const firstPlaceholder = 'What services do you offer?';
  const { mobileFullscreen, mobileCompact, toggleFullscreen } = useAskAiMobileFullscreen(open);

  useEffect(() => {
    if (enabled === true) {
      setIsEnabled(true);
      return;
    }
    if (enabled === false) {
      setIsEnabled(false);
      return;
    }
    let cancelled = false;
    apiService.getAskAiStatus(shortName)
      .then((data) => {
        if (!cancelled) setIsEnabled(Boolean(data.enabled));
      })
      .catch(() => {
        if (!cancelled) setIsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, shortName]);

  useEffect(() => {
    if (!attractAttention || open || !isEnabled || !shortName) {
      if (open || !isEnabled) {
        setAttractAttention(false);
        setBounceTick(null);
      }
      return;
    }
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setAttractAttention(false);
      setBounceTick(null);
      return;
    }

    let cancelled = false;
    let bounceTimer = 0;
    const stopAt = Date.now() + 3000;

    const fireBounce = () => {
      if (cancelled || Date.now() >= stopAt) {
        setAttractAttention(false);
        setBounceTick(null);
        return;
      }
      const duration = 360 + Math.random() * 480;
      setBounceTick({
        id: Date.now() + Math.random(),
        height: 5 + Math.random() * 13,
        duration,
      });
      const remaining = stopAt - Date.now();
      const rest = 120 + Math.random() * 520;
      const wait = duration + rest;
      if (remaining <= wait) {
        bounceTimer = window.setTimeout(() => {
          setAttractAttention(false);
          setBounceTick(null);
        }, Math.max(0, remaining));
        return;
      }
      bounceTimer = window.setTimeout(fireBounce, wait);
    };

    bounceTimer = window.setTimeout(fireBounce, 80 + Math.random() * 220);
    const stopTimer = window.setTimeout(() => {
      setAttractAttention(false);
      setBounceTick(null);
    }, 3000);

    return () => {
      cancelled = true;
      window.clearTimeout(bounceTimer);
      window.clearTimeout(stopTimer);
    };
  }, [attractAttention, open, isEnabled, shortName]);

  const persistHistory = useCallback((next: AskAiTurn[]) => {
    sessionStorage.setItem(storageKey, JSON.stringify(next.map((turn) => ({ role: turn.role, content: turn.content }))));
  }, [storageKey]);

  const persistExtras = useCallback((
    suggestions: Record<number, AskAiSuggestion>,
    bookings: Record<number, AskAiBookingStatus>
  ) => {
    suggestionsRef.current = suggestions;
    bookingsRef.current = bookings;
    if (Object.keys(suggestions).length === 0 && Object.keys(bookings).length === 0) {
      sessionStorage.removeItem(extrasKey);
      return;
    }
    sessionStorage.setItem(extrasKey, JSON.stringify({ suggestions, bookings }));
  }, [extrasKey]);

  const focusComposer = useCallback(() => {
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const persistContact = useCallback((next: AskAiContactDraft) => {
    if (next && (next.wanted || next.name || next.email || next.message)) {
      sessionStorage.setItem(contactKey, JSON.stringify(next));
    } else {
      sessionStorage.removeItem(contactKey);
    }
  }, [contactKey]);

  const persistBooking = useCallback((next: AskAiBookingDraft) => {
    if (next && (next.wanted || (next.serviceNames && next.serviceNames.length) || next.date || next.time || next.professionalId || next.professionalName || (next.serviceProfessionals && next.serviceProfessionals.length) || typeof next.sameProfessional === 'boolean' || next.quoteDetails || next.preferredSchedule || (next.quoteAttachmentIds && next.quoteAttachmentIds.length) || next.recurring || next.intervalWeeks || next.occurrenceCount)) {
      sessionStorage.setItem(bookingKey, JSON.stringify(next));
    } else {
      sessionStorage.removeItem(bookingKey);
    }
  }, [bookingKey]);

  const startFreshSession = useCallback(() => {
    resetAskAiSessionId(storageKey);
    const greeting: AskAiTurn[] = [{ role: 'assistant', content: sessionGreeting(user?.firstName, businessName) }];
    setHistory(greeting);
    setContactDraft({});
    setBookingDraft({});
    setSuggestionsByIndex({});
    setBookingByIndex({});
    persistHistory(greeting);
    persistContact({});
    persistBooking({});
    persistExtras({}, {});
    persistTenantAskHold(storageKey, false);
    persistTenantAskOpen(storageKey, false);
    setConversationFlagged(false);
    try {
      sessionStorage.removeItem(`${storageKey}:flagged`);
    } catch {
      // sessionStorage can be unavailable
    }
    setStatus('');
    setStatusError(false);
  }, [user?.firstName, businessName, persistHistory, persistContact, persistBooking, persistExtras, storageKey]);
  const startFreshSessionRef = useRef(startFreshSession);
  startFreshSessionRef.current = startFreshSession;

  useEffect(() => {
    if (!user?.firstName) return;
    setHistory((current) => {
      if (current.length !== 1 || current[0].role !== 'assistant') return current;
      if (current[0].content.includes(`${user.firstName},`)) return current;
      if (!/^Good (morning|afternoon|evening), /.test(current[0].content)) return current;
      const next = [{ role: 'assistant' as const, content: sessionGreeting(user.firstName, businessName) }];
      persistHistory(next);
      return next;
    });
  }, [user?.firstName, businessName, persistHistory]);

  useEffect(() => {
    const storedTurns = sessionStorage.getItem(storageKey);
    const hasStoredSession = Boolean(storedTurns && storedTurns !== '[]');
    const continuing = sessionStorage.getItem(TENANT_ASK_VISIT_KEY) === shortName
      || (hasStoredSession && isTenantPath(window.location.pathname, shortName));
    sessionStorage.setItem(TENANT_ASK_VISIT_KEY, shortName);
    tenantAskMounts[shortName] = (tenantAskMounts[shortName] || 0) + 1;

    if (continuing) {
      try {
        const parsed = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
        if (Array.isArray(parsed)) {
          setHistory(
            parsed
              .filter((turn: AskAiTurn) => turn && (turn.role === 'user' || turn.role === 'assistant') && typeof turn.content === 'string')
              .map((turn: AskAiTurn) => ({ role: turn.role, content: turn.content.trim() }))
              .filter((turn: AskAiTurn) => turn.content)
          );
        }
      } catch {
        setHistory([]);
      }
      try {
        const parsed = JSON.parse(sessionStorage.getItem(contactKey) || 'null');
        if (parsed && typeof parsed === 'object') {
          setContactDraft({
            wanted: Boolean(parsed.wanted),
            name: String(parsed.name || ''),
            email: String(parsed.email || ''),
            message: String(parsed.message || ''),
          });
        } else {
          setContactDraft({});
        }
      } catch {
        setContactDraft({});
      }
      try {
        const parsed = JSON.parse(sessionStorage.getItem(bookingKey) || 'null');
        if (parsed && typeof parsed === 'object') {
          setBookingDraft({
            wanted: Boolean(parsed.wanted),
            serviceNames: Array.isArray(parsed.serviceNames) ? parsed.serviceNames.map(String) : [],
            serviceIds: Array.isArray(parsed.serviceIds) ? parsed.serviceIds.map(String) : [],
            candidateServiceNames: Array.isArray(parsed.candidateServiceNames) ? parsed.candidateServiceNames.map(String) : [],
            date: String(parsed.date || ''),
            time: String(parsed.time || ''),
            notes: String(parsed.notes || ''),
            professionalId: String(parsed.professionalId || ''),
            professionalName: String(parsed.professionalName || ''),
            anyProfessional: Boolean(parsed.anyProfessional),
            sameProfessional: typeof parsed.sameProfessional === 'boolean' ? parsed.sameProfessional : undefined,
            serviceProfessionals: Array.isArray(parsed.serviceProfessionals)
              ? parsed.serviceProfessionals
                .filter((item: { serviceId?: string; professionalId?: string }) => item && item.serviceId && item.professionalId)
                .map((item: { serviceId: string; professionalId: string; professionalName?: string }) => ({
                  serviceId: String(item.serviceId),
                  professionalId: String(item.professionalId),
                  professionalName: item.professionalName ? String(item.professionalName) : undefined,
                }))
              : [],
            quoteDetails: String(parsed.quoteDetails || ''),
            preferredSchedule: String(parsed.preferredSchedule || ''),
            skipPreferredSchedule: Boolean(parsed.skipPreferredSchedule),
            quoteAttachmentIds: Array.isArray(parsed.quoteAttachmentIds) ? parsed.quoteAttachmentIds.map(String) : [],
            quoteImageUrls: Array.isArray(parsed.quoteImageUrls) ? parsed.quoteImageUrls.map(String) : [],
            recurring: Boolean(parsed.recurring),
            intervalWeeks: Number(parsed.intervalWeeks) > 0 ? Number(parsed.intervalWeeks) : undefined,
            occurrenceCount: Number(parsed.occurrenceCount) > 0 ? Number(parsed.occurrenceCount) : undefined,
            serviceAddress: parsed.serviceAddress && typeof parsed.serviceAddress === 'object'
              ? {
                  address: String(parsed.serviceAddress.address || ''),
                  city: String(parsed.serviceAddress.city || ''),
                  state: String(parsed.serviceAddress.state || ''),
                  zip: String(parsed.serviceAddress.zip || ''),
                  locationNotes: parsed.serviceAddress.locationNotes ? String(parsed.serviceAddress.locationNotes) : undefined,
                  locationLabel: parsed.serviceAddress.locationLabel ? String(parsed.serviceAddress.locationLabel) : undefined,
                }
              : undefined,
            rememberServiceAddress: typeof parsed.rememberServiceAddress === 'boolean' ? parsed.rememberServiceAddress : undefined,
          });
        } else {
          setBookingDraft({});
        }
      } catch {
        setBookingDraft({});
      }
      const extras = loadChatExtras(extrasKey);
      suggestionsRef.current = extras.suggestions;
      bookingsRef.current = extras.bookings;
      setSuggestionsByIndex(extras.suggestions);
      setBookingByIndex(extras.bookings);
      if (sessionStorage.getItem(`${storageKey}:open`) === '1') setOpen(true);
      const flaggedFor = sessionStorage.getItem(`${storageKey}:flagged`) || '';
      setConversationFlagged(flaggedFor !== '' && flaggedFor === getOrCreateAskAiSessionId(storageKey));
    } else {
      startFreshSessionRef.current();
    }

    return () => {
      tenantAskMounts[shortName] = Math.max(0, (tenantAskMounts[shortName] || 1) - 1);
      window.setTimeout(() => {
        if ((tenantAskMounts[shortName] || 0) > 0) return;
        if (isTenantPath(window.location.pathname, shortName)) return;
        if (shouldHoldTenantAsk(storageKey)) return;
        clearTenantAskStorage(storageKey);
        if (sessionStorage.getItem(TENANT_ASK_VISIT_KEY) === shortName) {
          sessionStorage.removeItem(TENANT_ASK_VISIT_KEY);
        }
      }, 100);
    };
  }, [shortName, storageKey, contactKey, bookingKey, extrasKey]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [history, open, pending]);

  const resetChat = () => {
    stopSpeaking();
    startFreshSession();
    persistTenantAskOpen(storageKey, true);
    setOpen(true);
    focusComposer();
  };

  const flagConversation = async () => {
    if (flagging || !history.some((turn) => turn.role === 'user' && turn.content.trim())) return;
    const dialog = document.getElementById('ask-dialog');
    if (!dialog) {
      setStatus('Could not capture the chat image. Please try again.');
      setStatusError(true);
      return;
    }
    setFlagging(true);
    setStatus('');
    setStatusError(false);
    const sessionId = getOrCreateAskAiSessionId(storageKey);
    try {
      const images = await captureAskAiChatImages(dialog);
      await apiService.flagAskAiConversation(shortName, {
        history: history.map((turn) => ({ role: turn.role, content: turn.content })),
        sessionId,
        pagePath: `${window.location.pathname}${window.location.search}`,
        images,
      });
      try {
        sessionStorage.setItem(`${storageKey}:flagged`, sessionId);
      } catch {
        // The S3 copy is already saved; the icon can still show flagged for this view.
      }
      setConversationFlagged(true);
      setStatus('Thanks. This conversation was flagged for review.');
      setStatusError(false);
    } catch (error: unknown) {
      const message = String((error as { response?: { data?: { message?: unknown } }; message?: unknown })?.response?.data?.message || (error as { message?: unknown })?.message || '').trim();
      setStatus(message || 'Could not flag this conversation. Please try again.');
      setStatusError(true);
    } finally {
      setFlagging(false);
    }
  };

  const openPanel = () => {
    lastFocusRef.current = document.activeElement as HTMLElement;
    persistTenantAskOpen(storageKey, true);
    setOpen(true);
    focusComposer();
  };

  const closePanel = () => {
    stopListening();
    stopSpeaking();
    persistTenantAskOpen(storageKey, false);
    setOpen(false);
  };

  useEffect(() => {
    const onOpen = (event: Event) => {
      if (!isEnabled) return;
      const requested = (event as CustomEvent<{ shortName?: string }>).detail?.shortName;
      if (requested && requested !== shortName) return;
      lastFocusRef.current = document.activeElement as HTMLElement;
      persistTenantAskOpen(storageKey, true);
      setOpen(true);
      focusComposer();
    };
    window.addEventListener(TENANT_ASK_AI_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(TENANT_ASK_AI_OPEN_EVENT, onOpen);
  }, [isEnabled, shortName, storageKey, focusComposer]);

  const applyBookingReply = (booking?: AskAiBookingStatus) => {
    if (!booking) return;
    if (!booking.wanted) {
      setBookingDraft({});
      persistBooking({});
      persistTenantAskHold(storageKey, false);
      return;
    }
    const next: AskAiBookingDraft = {
      wanted: true,
      serviceNames: (booking.services || []).map((service) => service.name),
      serviceIds: (booking.services || []).map((service) => service.id),
      candidateServiceNames: booking.candidateServiceNames || [],
      date: booking.date || '',
      time: booking.time || '',
      notes: booking.notes || '',
      professionalId: booking.professionalId || '',
      professionalName: booking.professionalName || '',
      anyProfessional: Boolean(booking.anyProfessional),
      sameProfessional: typeof booking.sameProfessional === 'boolean' ? booking.sameProfessional : undefined,
      serviceProfessionals: booking.serviceProfessionals || [],
      quoteDetails: booking.quoteDetails || '',
      preferredSchedule: booking.preferredSchedule || '',
      skipPreferredSchedule: Boolean(booking.skipPreferredSchedule),
      quoteAttachmentIds: booking.quoteAttachmentIds || [],
      quoteImageUrls: booking.quoteImageUrls || [],
      recurring: Boolean(booking.recurring),
      intervalWeeks: booking.intervalWeeks,
      occurrenceCount: booking.occurrenceCount,
      revisingServices: Boolean(booking.revisingServices),
      serviceAddress: booking.serviceAddress,
      rememberServiceAddress: booking.rememberServiceAddress,
      needsAddress: Boolean(booking.needsAddress),
    };
    setBookingDraft(next);
    persistBooking(next);
    persistTenantAskHold(storageKey, Boolean(booking.needsLogin || booking.needsPhone));
  };

  const ask = async (query: string, bookingOverride?: AskAiBookingDraft) => {
    if (pending || bookingBusy) return;
    stopListening();
    stopSpeaking();
    setSpeechError('');
    unlockBrowserSpeech();
    const prior = history.map((turn) => ({ role: turn.role, content: turn.content }));
    const bookingPayload = bookingOverride ? { ...bookingDraft, ...bookingOverride, wanted: true } : bookingDraft;
    setPending(true);
    setStatus('');
    setStatusError(false);
    setHistory((current) => [...current, { role: 'user', content: query }]);
    try {
      const data: AskAiReply = await apiService.askBusiness(shortName, {
        query,
        history: prior,
        contact: contactDraft,
        booking: bookingPayload,
        timeZone: getBrowserTimeZone(),
        pagePath: `${window.location.pathname}${window.location.search}`,
        sessionId: getOrCreateAskAiSessionId(storageKey),
      });
      const answer = data.answer || 'Here is what I can share.';
      void speakRef.current(answer);
      if (data.contact?.sent) {
        setContactDraft({});
        persistContact({});
      } else if (data.contact?.wanted) {
        const next = {
          wanted: true,
          name: data.contact.name || '',
          email: data.contact.email || '',
          message: data.contact.message || '',
        };
        setContactDraft(next);
        persistContact(next);
      }
      applyBookingReply(data.booking);
      setHistory((current) => {
        const next = [...current, { role: 'assistant' as const, content: answer }];
        persistHistory(next);
        const index = next.length - 1;
        const suggestion = (data.suggestions || [])[0];
        const suggestions = suggestion ? { ...suggestionsRef.current, [index]: suggestion } : suggestionsRef.current;
        const bookings = data.booking?.wanted
          ? { ...bookingsRef.current, [index]: data.booking as AskAiBookingStatus }
          : bookingsRef.current;
        setSuggestionsByIndex(suggestions);
        setBookingByIndex(bookings);
        persistExtras(suggestions, bookings);
        return next;
      });
    } catch (error: any) {
      setHistory((current) => current.slice(0, -1));
      setStatus(askAiRequestErrorMessage(error));
      setStatusError(true);
    } finally {
      setPending(false);
    }
  };
  askRef.current = ask;

  useEffect(() => {
    if (resumedBookingRef.current || pending || bookingBusy) return;
    if (!user) {
      resumedBookingRef.current = false;
      return;
    }
    const last = [...history].reverse().find((turn) => turn.role === 'assistant');
    if (!bookingDraft.wanted || !last) return;
    const needsResume = /have not (yet )?logged in|not logged in yet|sign in first|needs a phone number/i.test(last.content);
    if (!needsResume) return;
    if (!open) {
      persistTenantAskOpen(storageKey, true);
      setOpen(true);
      return;
    }
    resumedBookingRef.current = true;
    void askRef.current('Please continue with my booking.');
  }, [user, open, history, bookingDraft.wanted, pending, bookingBusy]);

  const confirmQuote = async (booking: AskAiBookingStatus, messageIndex: number) => {
    if (bookingBusy || !booking.ready || !booking.quote) return;
    if (!user) {
      setStatus('Please sign in first, then come back and I will help you send this quote request.');
      setStatusError(true);
      return;
    }
    if (!booking.businessId || !(booking.services || []).length || !String(booking.quoteDetails || '').trim()) {
      setStatus('I am missing quote details. Please tell me the service and job details again.');
      setStatusError(true);
      return;
    }
    setBookingBusy(true);
    setStatus('');
    setStatusError(false);
    const draft: AskAiBookingDraft = {
      wanted: true,
      serviceNames: (booking.services || []).map((service) => service.name),
      serviceIds: (booking.services || []).map((service) => service.id).filter(Boolean),
      quoteDetails: booking.quoteDetails || '',
      preferredSchedule: booking.preferredSchedule || '',
      skipPreferredSchedule: Boolean(booking.skipPreferredSchedule),
      quoteAttachmentIds: booking.quoteAttachmentIds || [],
      quoteImageUrls: booking.quoteImageUrls || [],
    };
    try {
      const verified = await apiService.confirmAskAiBooking(shortName, {
        booking: draft,
        timeZone: getBrowserTimeZone(),
        pagePath: `${window.location.pathname}${window.location.search}`,
        history: history.map((turn) => ({ role: turn.role, content: turn.content })),
        sessionId: getOrCreateAskAiSessionId(storageKey),
      });
      if (!verified.booking?.ready || !verified.booking.quote) {
        applyBookingReply(verified.booking);
        setHistory((current) => {
          const next = [...current, { role: 'assistant' as const, content: verified.answer || 'I could not confirm that quote request just now.' }];
          persistHistory(next);
          const bookings = {
            ...bookingsRef.current,
            [messageIndex]: { ...booking, ready: false },
            ...(verified.booking?.wanted ? { [next.length - 1]: verified.booking as AskAiBookingStatus } : {}),
          };
          setBookingByIndex(bookings);
          persistExtras(suggestionsRef.current, bookings);
          return next;
        });
        return;
      }
      const created = await apiService.createQuote({
        businessId: verified.booking.businessId || booking.businessId,
        services: (verified.booking.services || booking.services || []).map((service) => ({
          id: service.id,
          price: service.quotable ? null : (service.price != null && Number.isFinite(Number(service.price)) ? Number(service.price) : null),
        })),
        preferredSchedule: verified.booking.skipPreferredSchedule ? undefined : (verified.booking.preferredSchedule || undefined),
        customerProvidedDetails: verified.booking.quoteDetails || booking.quoteDetails,
        attachmentIds: (booking.quoteAttachmentIds && booking.quoteAttachmentIds.length > 0)
          ? booking.quoteAttachmentIds
          : verified.booking.quoteAttachmentIds,
        imageUrls: (booking.quoteImageUrls && booking.quoteImageUrls.length > 0)
          ? booking.quoteImageUrls
          : verified.booking.quoteImageUrls,
      });
      const quoteUrl = created?.id
        ? `/dashboard/customer/quote-requests/${created.id}`
        : '/dashboard/customer/quote-requests';
      setBookingDraft({});
      persistBooking({});
      setHistory((current) => {
        const next = [...current, { role: 'assistant' as const, content: 'Your quote request has been sent. The business will review it and get back to you.' }];
        persistHistory(next);
        const bookings = {
          ...bookingsRef.current,
          [messageIndex]: { ...booking, ready: false },
          [next.length - 1]: {
            ...verified.booking,
            ready: false,
            quoted: true,
            quote: true,
            quoteId: created?.id,
            quoteUrl,
          },
        };
        setBookingByIndex(bookings);
        persistExtras(suggestionsRef.current, bookings);
        return next;
      });
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.response?.data?.message || 'I could not send that quote request. Please try again or use the booking page.');
      setStatusError(true);
    } finally {
      setBookingBusy(false);
    }
  };

  const confirmBook = async (booking: AskAiBookingStatus, messageIndex: number) => {
    if (bookingBusy || !booking.ready) return;
    if (booking.quote) {
      await confirmQuote(booking, messageIndex);
      return;
    }
    if (!user) {
      setStatus('Please sign in first, then come back and I will help you book.');
      setStatusError(true);
      return;
    }
    if (!booking.businessId || !booking.assignments?.length || (!booking.scheduledAt && !booking.recurring)) {
      setStatus('I am missing booking details. Please tell me the service, date, and time again.');
      setStatusError(true);
      return;
    }
    setBookingBusy(true);
    setStatus('');
    setStatusError(false);
    const confirmTurn: AskAiTurn = { role: 'user', content: 'Confirm and Book' };
    const historyForRequest = [
      ...history.map((turn) => ({ role: turn.role, content: turn.content })),
      { role: 'user' as const, content: confirmTurn.content },
    ];
    setHistory((current) => {
      const next = [...current, confirmTurn];
      persistHistory(next);
      return next;
    });
    const draft: AskAiBookingDraft = {
      wanted: true,
      serviceNames: (booking.services || []).map((service) => service.name),
      serviceIds: (booking.services || []).map((service) => service.id).filter(Boolean),
      date: booking.date || '',
      time: booking.time || '',
      notes: booking.notes || '',
      professionalId: booking.professionalId || booking.assignments[0]?.professionalId || '',
      professionalName: booking.professionalName || '',
      sameProfessional: typeof booking.sameProfessional === 'boolean' ? booking.sameProfessional : undefined,
      serviceProfessionals: booking.serviceProfessionals
        || (booking.assignments || [])
          .filter((item) => item.professionalId)
          .map((item) => ({
            serviceId: item.serviceId,
            professionalId: item.professionalId as string,
            professionalName: (booking.services || []).find((service) => service.id === item.serviceId)?.professionalName,
          })),
      recurring: Boolean(booking.recurring),
      intervalWeeks: booking.intervalWeeks,
      occurrenceCount: booking.occurrenceCount,
      serviceAddress: booking.serviceAddress,
      rememberServiceAddress: booking.rememberServiceAddress !== false,
    };
    const showUnbookable = (reply: AskAiReply) => {
      const answer = reply.answer || 'That time is no longer available. Please pick another slot.';
      applyBookingReply(reply.booking);
      setHistory((current) => {
        const next = [...current, { role: 'assistant' as const, content: answer }];
        persistHistory(next);
        const bookings = {
          ...bookingsRef.current,
          [messageIndex]: { ...booking, ready: false },
          ...(reply.booking?.wanted ? { [next.length - 1]: reply.booking as AskAiBookingStatus } : {}),
        };
        setBookingByIndex(bookings);
        persistExtras(suggestionsRef.current, bookings);
        return next;
      });
    };
    try {
      const verified = await apiService.confirmAskAiBooking(shortName, {
        booking: draft,
        timeZone: getBrowserTimeZone(),
        pagePath: `${window.location.pathname}${window.location.search}`,
        history: historyForRequest,
        sessionId: getOrCreateAskAiSessionId(storageKey),
      });
      if (!verified.booking?.ready || !verified.booking.assignments?.length) {
        showUnbookable(verified);
        return;
      }
      const series = Boolean(verified.booking.recurring)
        && Number(verified.booking.intervalWeeks) >= 1
        && Number(verified.booking.occurrenceCount) >= 1
        && Boolean(verified.booking.date)
        && Boolean(verified.booking.time);
      const scheduledAt = verified.booking.scheduledAt || '';
      if (!series && !scheduledAt) {
        showUnbookable(verified);
        return;
      }
      try {
        let appointmentId = '';
        let depositNum = 0;
        let visitCount = 1;
        if (series) {
          const created = await apiService.createRecurringAppointments({
            businessId: verified.booking.businessId || booking.businessId || '',
            customerId: user.id,
            mode: 'STRICT',
            intervalWeeks: Number(verified.booking.intervalWeeks),
            occurrenceCount: Number(verified.booking.occurrenceCount),
            startDate: verified.booking.date || '',
            startTime: verified.booking.time || '',
            clientTimeZone: getBrowserTimeZone(),
            notes: verified.booking.notes || booking.notes,
            assignments: verified.booking.assignments,
            serviceAddress: verified.booking.serviceAddress || booking.serviceAddress,
            rememberServiceAddress: verified.booking.rememberServiceAddress !== false,
            bookedViaAi: true,
            aiChatSessionId: getOrCreateAskAiSessionId(storageKey),
          });
          appointmentId = created.appointmentIds?.[0] || '';
          visitCount = Array.isArray(created.appointmentIds) ? created.appointmentIds.length : Number(verified.booking.occurrenceCount);
          depositNum = Number(
            created.firstAppointmentRequiredDepositAmount
            ?? verified.booking.depositAmount
            ?? booking.depositAmount
            ?? 0
          );
        } else {
          const created = await apiService.createAppointment({
            businessId: verified.booking.businessId || booking.businessId,
            customerId: user.id,
            scheduledAt,
            notes: verified.booking.notes || booking.notes,
            bookingContext: 'customer',
            assignments: verified.booking.assignments,
            serviceAddress: verified.booking.serviceAddress || booking.serviceAddress,
            rememberServiceAddress: verified.booking.rememberServiceAddress !== false,
            bookedViaAi: true,
            aiChatSessionId: getOrCreateAskAiSessionId(storageKey),
          });
          appointmentId = created.id;
          depositNum = Number(created.requiredDepositAmount ?? verified.booking.depositAmount ?? booking.depositAmount ?? 0);
        }
        const depositUrl = depositNum > 0 && appointmentId ? `/dashboard/customer/appointments/${appointmentId}/deposit` : undefined;
        const appointmentUrl = appointmentId ? `/dashboard/customer/appointments/${appointmentId}` : undefined;
        const followUp = series
          ? (depositNum > 0
            ? `Your recurring series is booked (${visitCount} visit${visitCount === 1 ? '' : 's'}). Pay the deposit to hold it.`
            : `Your recurring series is booked (${visitCount} visit${visitCount === 1 ? '' : 's'}). I saved it on your appointments page.`)
          : (depositNum > 0
            ? 'Your appointment is booked. Pay the deposit to hold it.'
            : 'Your appointment is booked. I saved it on your appointments page.');
        setBookingDraft({});
        persistBooking({});
        setHistory((current) => {
          const next = [...current, { role: 'assistant' as const, content: followUp }];
          persistHistory(next);
          const bookings = {
            ...bookingsRef.current,
            [messageIndex]: { ...booking, ready: false },
            [next.length - 1]: {
              ...verified.booking,
              ready: false,
              booked: true,
              appointmentId: appointmentId || undefined,
              depositRequired: depositNum > 0,
              depositAmount: depositNum > 0 ? depositNum : undefined,
              depositUrl,
              appointmentUrl,
            },
          };
          setBookingByIndex(bookings);
          persistExtras(suggestionsRef.current, bookings);
          return next;
        });
      } catch (error: any) {
        const status = Number(error?.response?.status);
        if (status === 409) {
          const again = await apiService.confirmAskAiBooking(shortName, {
            booking: draft,
            timeZone: getBrowserTimeZone(),
            pagePath: `${window.location.pathname}${window.location.search}`,
            history: historyForRequest,
            sessionId: getOrCreateAskAiSessionId(storageKey),
          });
          showUnbookable(again);
          return;
        }
        setStatus(error?.response?.data?.error || error?.response?.data?.message || 'I could not complete that booking. Please try another time or use the booking page.');
        setStatusError(true);
      }
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.response?.data?.message || 'I could not confirm that booking just now. Please try again.');
      setStatusError(true);
    } finally {
      setBookingBusy(false);
    }
  };

  useEffect(() => {
    if (!open || pending || bookingBusy || voice.listening) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open, pending, bookingBusy, history.length, voice.listening]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const query = input.trim();
    if (!query) {
      setStatus('Type a question about this business.');
      setStatusError(true);
      return;
    }
    setInput('');
    void ask(query);
  };

  if (!isEnabled || !shortName) return null;

  const hasChat = history.length > 0;
  const hasUserChat = history.some((turn) => turn.role === 'user');
  const vars = {
    ['--ask-accent' as string]: accentColor,
    ['--ask-accent-ink' as string]: accentInk,
    ['--ask-ink' as string]: textColor,
    ['--ask-muted' as string]: `${textColor}99`,
    ['--ask-surface' as string]: '#ffffff',
    ['--ask-line' as string]: `${textColor}22`,
  } as React.CSSProperties;

  return (
    <>
    <style>{`
      @keyframes tenant-ask-ai-nudge {
        0% { transform: translateY(0); }
        38% { transform: translateY(var(--ask-bounce-y, -10px)); }
        100% { transform: translateY(0); }
      }
      @keyframes tenant-ask-ai-vibrate {
        0%, 100% { transform: translateX(0); }
        12% { transform: translateX(-3px); }
        24% { transform: translateX(3px); }
        36% { transform: translateX(-3px); }
        48% { transform: translateX(3px); }
        60% { transform: translateX(-2px); }
        72% { transform: translateX(2px); }
        84% { transform: translateX(-1px); }
        92% { transform: translateX(1px); }
      }
      @keyframes tenant-ask-ai-ring {
        0% { transform: scale(0.94); opacity: 0.6; }
        100% { transform: scale(1.32); opacity: 0; }
      }
      .tenant-ask-ai-bounce {
        animation: tenant-ask-ai-nudge var(--ask-bounce-ms, 520ms) cubic-bezier(0.22, 1, 0.36, 1);
      }
      .tenant-ask-ai-vibrate {
        animation: tenant-ask-ai-vibrate var(--ask-bounce-ms, 520ms) linear;
      }
      .tenant-ask-ai-vibrate::after {
        content: '';
        position: absolute;
        inset: -4px;
        border-radius: 9999px;
        border: 2px solid var(--ask-accent);
        pointer-events: none;
        animation: tenant-ask-ai-ring var(--ask-bounce-ms, 520ms) ease-out;
      }
      @media (prefers-reduced-motion: reduce) {
        .tenant-ask-ai-bounce,
        .tenant-ask-ai-vibrate,
        .tenant-ask-ai-vibrate::after {
          animation: none;
        }
      }
    `}</style>
    <AskAiMobileScrim show={mobileCompact} onClose={closePanel} />
    <div
      ref={dockRef}
      className={`fixed flex flex-col-reverse items-end pointer-events-none ${
        mobileFullscreen
          ? 'inset-0 z-[1100] gap-0'
          : `${mobileCompact ? 'z-[1100]' : 'z-50'} gap-3 ${
            placement === 'booking'
              ? 'right-[max(1.15rem,env(safe-area-inset-right))] bottom-24 sm:bottom-28'
              : 'right-[max(1.15rem,env(safe-area-inset-right))] bottom-[max(1.15rem,env(safe-area-inset-bottom))]'
          }`
      }`}
      style={{ ...vars, width: mobileFullscreen ? '100%' : 'min(23rem, calc(100vw - 2.3rem))' }}
    >
      <div
        key={bounceTick ? `ask-ai-bounce-${bounceTick.id}` : 'ask-ai-idle'}
        className={`${open ? 'max-md:hidden' : ''} ${bounceTick && !open ? 'tenant-ask-ai-bounce' : ''} pointer-events-auto self-end`}
        style={{
          ['--ask-bounce-y' as string]: bounceTick ? `-${bounceTick.height.toFixed(1)}px` : '-10px',
          ['--ask-bounce-ms' as string]: bounceTick ? `${Math.round(bounceTick.duration)}ms` : '520ms',
        }}
      >
        <button
          type="button"
          className={`${bounceTick && !open ? 'tenant-ask-ai-vibrate' : ''} relative inline-flex items-center gap-2 rounded-full px-4 py-3 sm:py-3 sm:pr-4 shadow-lg font-semibold text-sm`}
          style={{ background: accentColor, color: accentInk }}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls="ask-dialog"
          onClick={() => (open ? closePanel() : openPanel())}
        >
          <AaCoreAiName
            ask
            color={accentInk}
            aiColor={accentInk === '#ffffff' ? '#FDE68A' : accentColor}
            iconClassName="w-5 h-5"
            className="text-sm"
          />
        </button>
      </div>

      {open && (
        <div className={`pointer-events-auto w-full ${mobileFullscreen ? 'h-full' : ''}`}>
          <div
            id="ask-dialog"
            role="dialog"
            aria-modal={mobileCompact || mobileFullscreen}
            aria-labelledby="ask-dialog-title"
            className="flex flex-col w-full overflow-hidden bg-white border shadow-2xl"
            style={{
              height: mobileFullscreen ? '100%' : 'min(78vh, 36rem)',
              color: textColor,
              borderColor: `${textColor}22`,
              borderRadius: mobileFullscreen ? 0 : 16,
            }}
          >
            <header className={`flex flex-col gap-1 px-4 pb-3 flex-shrink-0 ${mobileFullscreen ? 'pt-[max(1rem,env(safe-area-inset-top))]' : 'pt-4'}`}>
              <div className="flex justify-between items-start gap-3">
                <h2 id="ask-dialog-title" className="text-lg font-semibold m-0 tracking-tight min-w-0">
                  <AaCoreAiName ask color={textColor} aiColor={accentColor} iconClassName="w-5 h-5" className="text-lg" />
                </h2>
                <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  className="w-8 h-8 border bg-transparent inline-flex items-center justify-center cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    borderColor: `${textColor}22`,
                    color: conversationFlagged ? '#b45309' : textColor,
                    borderRadius: 8,
                  }}
                  aria-label={conversationFlagged ? 'Update flagged conversation' : 'Flag this conversation'}
                  aria-pressed={conversationFlagged}
                  title={
                    conversationFlagged
                      ? 'Flagged for review. Click to save the latest messages and a snapshot.'
                      : hasUserChat
                        ? 'Flag this conversation for review'
                        : 'Start a conversation before flagging it'
                  }
                  disabled={flagging || !hasUserChat}
                  onClick={() => void flagConversation()}
                >
                  <Flag className="w-4 h-4" fill={conversationFlagged ? 'currentColor' : 'none'} />
                </button>
                {voice.canSpeak ? (
                  <button
                    type="button"
                    className="w-8 h-8 border bg-transparent inline-flex items-center justify-center cursor-pointer"
                    style={{ borderColor: `${textColor}22`, color: voice.speaking ? accentColor : textColor, borderRadius: 8 }}
                    aria-label={voice.voiceReplies ? 'Mute spoken replies' : 'Unmute spoken replies'}
                    aria-pressed={voice.voiceReplies}
                    title={voice.voiceReplies ? 'Mute spoken replies' : 'Speak replies aloud'}
                    onClick={voice.toggleVoiceReplies}
                  >
                    {voice.voiceReplies ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </button>
                ) : null}
                {hasChat && (
                  <button
                    type="button"
                    className="h-8 px-3 text-xs inline-flex items-center justify-center whitespace-nowrap shrink-0 border bg-transparent cursor-pointer box-border"
                    style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                    onClick={resetChat}
                  >
                    New
                  </button>
                )}
                <AskAiMobileFullscreenButton
                  fullscreen={mobileFullscreen}
                  onToggle={toggleFullscreen}
                  textColor={textColor}
                />
                <button
                  type="button"
                  className="w-8 h-8 border bg-transparent text-xl leading-none cursor-pointer"
                  style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                  aria-label="Close Ask AACore AI"
                  onClick={closePanel}
                >
                  ×
                </button>
                </div>
              </div>
              <p className="w-full mt-0 mb-0 text-sm" style={{ color: `${textColor}99` }}>
                Services & Booking at {businessName}
              </p>
            </header>

            <div ref={threadRef} data-ask-thread="true" className="flex-1 min-h-0 overflow-auto flex flex-col gap-2 px-4 pb-2" aria-live="polite">
              {history.map((turn, index) => {
                const suggestion = suggestionsByIndex[index];
                const booking = bookingByIndex[index];
                const isUser = turn.role === 'user';
                return (
                  <div
                    key={`${turn.role}-${index}`}
                    className={`max-w-[96%] px-3 py-2 text-sm leading-relaxed ${isUser ? 'self-end' : 'self-start border'}`}
                    style={
                      isUser
                        ? { background: accentColor, color: accentInk, borderRadius: 18 }
                        : { borderColor: `${textColor}22`, color: textColor, borderRadius: 18, minWidth: booking?.ready || booking?.booked || booking?.quoted || booking?.quote || (booking?.serviceGroups && booking.serviceGroups.length > 0) || (booking?.technicianGroups && booking.technicianGroups.length > 0) ? '16.5rem' : undefined }
                    }
                  >
                    {!isUser && (
                      <AaCoreAiName color={`${textColor}99`} aiColor={accentColor} className="mb-1.5" />
                    )}
                    {isUser ? (
                      <p className="m-0 whitespace-pre-wrap">{turn.content}</p>
                    ) : (
                      <AskAiRichText text={turn.content} accentColor={accentColor} />
                    )}
                    {!isUser && booking?.quote && booking.quoteInstructions && booking.quoteInstructions.length > 0 && !booking.ready && !booking.quoted && (
                      <QuoteInstructions items={booking.quoteInstructions} textColor={textColor} />
                    )}
                    {!isUser && (booking?.ready || booking?.booked || booking?.quoted || booking?.revising || (booking?.recurring && (booking.recurringOccurrences || []).length > 0)) && (
                      <AskAiBookingReviewCard
                        booking={booking}
                        textColor={textColor}
                        title={
                          booking.quoted
                            ? 'Quote request sent'
                            : booking.quote
                              ? 'Quote request summary'
                              : booking.booked
                                ? (booking.recurring ? 'Recurring series booked' : 'Appointment booked')
                                : booking.recurring
                                  ? 'Recurring series'
                                  : 'Appointment summary'
                        }
                      />
                    )}
                    {!isUser && (booking?.ready || booking?.revising) && !booking.booked && !booking.quoted && (
                      <BookingChangeLinks
                        accentColor={accentColor}
                        accentInk={accentInk}
                        textColor={textColor}
                        quoteMode={Boolean(booking.quote)}
                        seriesMode={Boolean(booking.recurring)}
                        onSite={Boolean(
                          booking.serviceAddress
                          || booking.needsAddress
                          || (booking.services || []).some((service) => /ON_SITE|ONSITE/i.test(String(service.serviceLocation || '')))
                        )}
                        disabled={pending || bookingBusy || index < history.length - 1}
                        onChange={(query, override) => void ask(query, override)}
                      />
                    )}
                    {!isUser && booking?.quote && booking.ready && !booking.quoted && (
                      <QuoteImageAttach
                        booking={booking}
                        accentColor={accentColor}
                        accentInk={accentInk}
                        textColor={textColor}
                        disabled={pending || bookingBusy || index < history.length - 1}
                        onUploaded={(attachmentIds, imageUrls) => {
                          const nextBooking = { ...booking, quoteAttachmentIds: attachmentIds, quoteImageUrls: imageUrls };
                          const bookings = { ...bookingsRef.current, [index]: nextBooking };
                          setBookingByIndex(bookings);
                          persistExtras(suggestionsRef.current, bookings);
                          const nextDraft: AskAiBookingDraft = {
                            ...bookingDraft,
                            wanted: true,
                            quoteAttachmentIds: attachmentIds,
                            quoteImageUrls: imageUrls,
                          };
                          setBookingDraft(nextDraft);
                          persistBooking(nextDraft);
                        }}
                      />
                    )}
                    {!isUser && suggestion && !booking?.wanted && (suggestion.primary?.url || (suggestion.actions || []).some((action) => action.url)) && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {suggestion.primary?.url && (
                          <a
                            href={suggestion.primary.url}
                            className="text-xs font-semibold no-underline px-2 py-1"
                            style={{ background: accentColor, color: accentInk, borderRadius: 8 }}
                          >
                            {suggestion.primary.label}
                          </a>
                        )}
                        {(suggestion.actions || []).filter((action) => action.url).map((action) => (
                          <a
                            key={action.url}
                            href={action.url}
                            className="text-xs font-semibold no-underline px-2 py-1 border"
                            style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                          >
                            {action.label}
                          </a>
                        ))}
                      </div>
                    )}
                    {!isUser && booking?.serviceGroups && booking.serviceGroups.length > 0 && !booking.ready && !booking.booked && (
                      <ServicePicker
                        key={`services-${(booking.services || []).map((service) => service.id).join(',')}-${booking.serviceGroups.length}`}
                        groups={booking.serviceGroups}
                        preselectedIds={(booking.services || []).map((service) => service.id)}
                        accentColor={accentColor}
                        accentInk={accentInk}
                        textColor={textColor}
                        disabled={pending || bookingBusy || index < history.length - 1}
                        onContinue={(services) => {
                          const names = services.map((service) => service.name);
                          const query = names.length === 1
                            ? `I'd like to book ${names[0]}`
                            : `I'd like to book ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
                          void ask(query.length > 700 ? 'I’d like to book the selected services.' : query, {
                            wanted: true,
                            serviceNames: names,
                            serviceIds: services.map((service) => service.id),
                            replaceServices: true,
                            candidateServiceNames: [],
                            serviceProfessionals: [],
                          });
                        }}
                      />
                    )}
                    {!isUser && booking?.technicianGroups && booking.technicianGroups.length > 0 && !booking.ready && !booking.booked && !booking.quote && (
                      <TechnicianPicker
                        groups={booking.technicianGroups}
                        accentColor={accentColor}
                        accentInk={accentInk}
                        textColor={textColor}
                        disabled={pending || bookingBusy || index < history.length - 1}
                        onContinue={(picks) => {
                          const query = picks.length === 1
                            ? `I'd like ${picks[0].professionalName}`
                            : `I'd like ${picks.map((pick) => pick.professionalName).join(' and ')}`;
                          void ask(query, {
                            wanted: true,
                            sameProfessional: false,
                            serviceProfessionals: picks,
                            professionalId: '',
                            professionalName: '',
                          });
                        }}
                      />
                    )}
                    {!isUser && booking && (booking.dateOptions?.length || booking.showCalendar) && !booking.ready && !booking.booked && !booking.quote && !(booking.technicianGroups && booking.technicianGroups.length > 0) && !(booking.missing || []).some((item) => item === 'intervalWeeks' || item === 'occurrenceCount' || item === 'recurringSlot' || item === 'serviceAddress') && (
                      <DateChoicePicker
                        options={booking.dateOptions || []}
                        showCalendar={booking.showCalendar}
                        accentColor={accentColor}
                        accentInk={accentInk}
                        textColor={textColor}
                        disabled={pending || bookingBusy || index < history.length - 1}
                        onPick={(ymd, label) => {
                          void ask(label || ymd, { wanted: true, date: ymd });
                        }}
                      />
                    )}
                    {!isUser && booking && (!booking.serviceGroups || booking.serviceGroups.length === 0) && (!booking.technicianGroups || booking.technicianGroups.length === 0) && !booking.ready && !booking.booked && !booking.quoted && !booking.revising && (
                      <>
                        {!((booking.missing || []).some((item) => item === 'intervalWeeks' || item === 'occurrenceCount' || item === 'recurringSlot' || item === 'serviceAddress'))
                          && ((booking.availableTimes && booking.availableTimes.length > 0) || (booking.chips || []).some(looksLikeTimeChip)) && (
                          <TimeSlotGrid
                            times={
                              booking.availableTimes && booking.availableTimes.length > 0
                                ? booking.availableTimes
                                : (booking.chips || []).filter(looksLikeTimeChip)
                            }
                            textColor={textColor}
                            disabled={pending || bookingBusy || index < history.length - 1}
                            onPick={(time, label) => {
                              const hhmm = /^([01]?\d|2[0-3]):[0-5]\d$/.test(time) ? time : undefined;
                              void ask(label, hhmm ? { wanted: true, time: hhmm } : { wanted: true });
                            }}
                          />
                        )}
                        {(booking.chips || []).filter((chip) => !looksLikeTimeChip(chip)).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {(booking.chips || []).filter((chip) => !looksLikeTimeChip(chip)).map((chip) => (
                              <button
                                key={chip}
                                type="button"
                                disabled={pending || bookingBusy || index < history.length - 1}
                                className="text-xs bg-transparent border rounded-full px-2 py-1 cursor-pointer disabled:opacity-60"
                                style={{ borderColor: `${textColor}22`, color: textColor }}
                                onClick={() => {
                                  if (chip === 'Same technician' || chip === 'Same professional') {
                                    void ask(chip, {
                                      wanted: true,
                                      sameProfessional: true,
                                      serviceProfessionals: [],
                                    });
                                    return;
                                  }
                                  if (chip === 'Different technicians' || chip === 'Different professionals') {
                                    void ask(chip, { wanted: true, sameProfessional: false });
                                    return;
                                  }
                                  void ask(chip);
                                }}
                              >
                                {toProfessionalWording(chip)}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    {!isUser && booking?.needsLogin && booking.loginUrl && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <a
                          href={booking.loginUrl}
                          className="text-xs font-semibold no-underline px-2 py-1"
                          style={{ background: accentColor, color: accentInk, borderRadius: 8 }}
                        >
                          Log in
                        </a>
                        {booking.bookingUrl && (
                          <a
                            href={booking.bookingUrl}
                            className="text-xs font-semibold no-underline px-2 py-1 border"
                            style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                          >
                            Book on site
                          </a>
                        )}
                      </div>
                    )}
                    {!isUser && booking?.needsPhone && booking.phoneUrl && (
                      <div className="mt-2">
                        <a
                          href={booking.phoneUrl}
                          className="text-xs font-semibold no-underline px-2 py-1"
                          style={{ background: accentColor, color: accentInk, borderRadius: 8 }}
                        >
                          Add phone number
                        </a>
                      </div>
                    )}
                    {!isUser && booking?.useBookingPage && booking.bookingUrl && (
                      <div className="mt-2">
                        <a
                          href={booking.bookingUrl}
                          className="text-xs font-semibold no-underline px-2 py-1"
                          style={{ background: accentColor, color: accentInk, borderRadius: 8 }}
                        >
                          Continue on booking page
                        </a>
                      </div>
                    )}
                    {!isUser && booking?.ready && !booking.quoted && (
                      <div className="mt-3">
                        <button
                          type="button"
                          className="text-sm font-semibold px-3 py-2 w-full cursor-pointer disabled:opacity-60"
                          style={{ background: accentColor, color: accentInk, borderRadius: 10, border: 0 }}
                          disabled={pending || bookingBusy || index < history.length - 1}
                          onClick={() => void confirmBook(booking, index)}
                        >
                          {bookingBusy
                            ? (booking.quote ? 'Sending…' : 'Booking…')
                            : (booking.quote ? 'Confirm quote request' : 'Confirm and Book')}
                        </button>
                      </div>
                    )}
                    {!isUser && booking?.quoted && booking.quoteUrl && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <a
                          href={booking.quoteUrl}
                          className="text-xs font-semibold no-underline px-2 py-1"
                          style={{ background: accentColor, color: accentInk, borderRadius: 8 }}
                        >
                          View quote request
                        </a>
                      </div>
                    )}
                    {!isUser && booking?.booked && (booking.depositUrl || booking.appointmentUrl) && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {booking.depositUrl && (
                          <a
                            href={booking.depositUrl}
                            className="text-xs font-semibold no-underline px-2 py-1"
                            style={{ background: accentColor, color: accentInk, borderRadius: 8 }}
                          >
                            Pay deposit
                          </a>
                        )}
                        {booking.appointmentUrl && (
                          <a
                            href={booking.appointmentUrl}
                            className="text-xs font-semibold no-underline px-2 py-1 border"
                            style={{ borderColor: `${textColor}22`, color: textColor, borderRadius: 8 }}
                          >
                            View appointment
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {pending && (
                <div
                  className="self-start max-w-[92%] px-3 py-2 text-sm italic border border-dashed"
                  style={{ borderColor: `${textColor}22`, color: `${textColor}99`, borderRadius: 18 }}
                >
                  Thinking…
                </div>
              )}
            </div>

            {!hasUserChat && (
              <p className="text-sm px-4 pb-2 m-0 flex-shrink-0" style={{ color: `${textColor}99` }}>
                Try:
                {[
                  { label: 'Business hours', query: 'What are your hours?' },
                  { label: 'Services', query: 'What services do you offer?' },
                  { label: 'Help me book an appointment', query: 'Help me book an appointment' },
                  { label: 'Help me request a quote', query: 'Help me request a quote' },
                ].map((hint) => (
                  <button
                    key={hint.label}
                    type="button"
                    className="text-xs bg-transparent border rounded-full px-2 py-0.5 mr-1 mt-1 cursor-pointer"
                    style={{ borderColor: `${textColor}22`, color: textColor }}
                    onClick={() => void ask(hint.query)}
                  >
                    {hint.label}
                  </button>
                ))}
              </p>
            )}

            <form className={`flex items-end gap-2 px-4 pt-3 border-t flex-shrink-0 ${mobileFullscreen ? 'pb-[max(0.75rem,env(safe-area-inset-bottom))]' : 'pb-3'}`} style={{ borderColor: `${textColor}22` }} onSubmit={submit}>
              <label className="block min-w-0 flex-1">
                <span className="sr-only">Ask AACore AI</span>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(event) => {
                    listenBaseRef.current = event.target.value;
                    setInput(event.target.value);
                  }}
                  maxLength={800}
                  autoComplete="off"
                  placeholder={voice.listening ? 'Listening…' : hasUserChat ? 'Ask a follow-up…' : firstPlaceholder}
                  className="w-full px-3 py-2 border text-sm"
                  style={{
                    color: textColor,
                    borderColor: voice.listening ? '#e3a3a3' : `${textColor}22`,
                    borderRadius: 10,
                    background: '#fff',
                    boxShadow: voice.listening ? '0 0 0 2px #fde8e8' : undefined,
                  }}
                  disabled={pending || bookingBusy}
                />
              </label>
              {voice.canListen ? (
                <button
                  type="button"
                  className="w-10 h-10 shrink-0 inline-flex items-center justify-center border-0 cursor-pointer disabled:opacity-60"
                  style={{
                    background: voice.listening ? '#fde8e8' : `${accentColor}14`,
                    color: voice.listening ? '#b42318' : accentColor,
                    borderRadius: 10,
                  }}
                  aria-label={voice.listening ? 'Stop voice input' : 'Speak your message'}
                  aria-pressed={voice.listening}
                  title={voice.listening ? 'Stop listening' : 'Talk to Ask AACore AI'}
                  disabled={pending || bookingBusy}
                  onClick={() => {
                    if (voice.listening) {
                      voice.stopListening();
                      return;
                    }
                    listenBaseRef.current = input;
                    voice.startListening();
                  }}
                >
                  <Mic className="w-5 h-5" />
                </button>
              ) : null}
              <button
                type="submit"
                disabled={pending || bookingBusy}
                className="font-semibold px-4 py-2 text-sm disabled:opacity-60 shrink-0"
                style={{ background: accentColor, color: accentInk, borderRadius: 10 }}
              >
                Send
              </button>
            </form>
            {(status || voice.speechError) && (
              <p className={`px-4 pb-3 m-0 text-sm flex-shrink-0 ${statusError ? 'text-red-700' : ''}`} style={statusError ? undefined : { color: `${textColor}99` }}>
                {status || voice.speechError}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default TenantAskAiWidget;
